import type { PayloadAction } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import { processTrips } from 'src/helper/work-order-utils';
import type { GroupedDataMetaItem } from 'src/redux/list-view/types';

// ---------------------------------------------------------------------------
// Shared per-group listing reducers
//
// The grouped listings (work-order, service-request, quotes, user-management)
// all page each group independently via a `getXForGroup` thunk. The bookkeeping
// around `groupedData` (id lists) and `groupedDataMeta` (loading/paging/error
// flags) is identical across domains, so it lives here once. Each slice only
// supplies its own item→source normalization; everything below is shared.
// ---------------------------------------------------------------------------

type GroupFetchMeta = { current_page?: number; next?: string | null } | undefined;

/**
 * Commit a fetched page of a single group: replace on page 1, append+dedupe on
 * later pages, and refresh the group's meta. Dedupe guards against a work order
 * / SR / quote id reappearing across a page boundary (server reorder, overlap,
 * or a Pusher-driven refetch), which would otherwise render a duplicate row and
 * collide React keys.
 */
export const applyGroupedDataPage = (
  state: any,
  groupKey: string,
  newIds: string[],
  meta: GroupFetchMeta,
  attemptId?: number,
) => {
  // Drop a settling success whose attempt no longer matches the group's current
  // one — e.g. a fetch issued under the previous filter resolving after the
  // filter changed. Applying it would render stale rows and latch isFetched so
  // the group never refetches under the new filter.
  const currentAttemptId = (state.groupedDataMeta[groupKey] as GroupedDataMetaItem | undefined)
    ?.attemptId;
  if (attemptId !== currentAttemptId) return;

  const page = meta?.current_page ?? 1;
  state.groupedData[groupKey] =
    page === 1 ? newIds : Array.from(new Set([...(state.groupedData[groupKey] ?? []), ...newIds]));

  const nextMeta: GroupedDataMetaItem = {
    isLoading: false,
    isFetched: true,
    currentPage: meta?.current_page ?? 1,
    hasMore: Boolean(meta?.next),
    hasError: false,
    attemptId,
  };
  state.groupedDataMeta[groupKey] = nextMeta;
};

/**
 * Mark a group's fetch as failed. Latches `hasError` (drives the inline error +
 * Retry UI in GroupingWrapper) and always clears `isLoading` — creating the meta
 * entry if it was wiped by a concurrent clear-data, so the group can never get
 * stuck showing a skeleton after a rejection.
 *
 * An aborted first-page fetch (the group scrolled out of view / was collapsed
 * mid-flight) or an auth-canceled request is NOT a real failure: reset the meta
 * to the un-fetched state (no error) so the group's in-view observer re-arms and
 * the fetch restarts when it returns to view.
 */
export const applyGroupFetchRejected = (state: any, action: PayloadAction<unknown>) => {
  const payload = action.payload as Record<string, unknown>;
  const groupKey = payload?._group_key as string | undefined;
  if (!groupKey) return;
  const existing = state.groupedDataMeta[groupKey] as GroupedDataMetaItem | undefined;
  // Ignore a settle from a superseded attempt (its abort/cancel produced this
  // rejection after a newer attempt already took over). Resetting here would
  // clobber the newer attempt's loading meta and re-arm the in-view observer —
  // the self-sustaining refetch loop this guard exists to break.
  const attemptId = payload?._attempt as number | undefined;
  if (attemptId !== existing?.attemptId) return;
  if (isCanceledError(action.payload)) {
    // A cancel of the CURRENT attempt (scrolled out / collapsed mid-flight):
    // reset to un-fetched (no error) so the observer re-arms and refetches on
    // return. Clearing attemptId keeps a matching-token guard from mistaking a
    // future stale settle for the current one.
    state.groupedDataMeta[groupKey] = {
      isFetched: existing?.isFetched ?? false,
      currentPage: existing?.currentPage ?? 0,
      hasMore: existing?.hasMore ?? false,
      isLoading: false,
      hasError: false,
      attemptId: undefined,
    };
    return;
  }
  const nextMeta: GroupedDataMetaItem = {
    isFetched: existing?.isFetched ?? false,
    currentPage: existing?.currentPage ?? 0,
    hasMore: existing?.hasMore ?? false,
    isLoading: false,
    hasError: true,
    attemptId,
  };
  state.groupedDataMeta[groupKey] = nextMeta;
};

/**
 * `setGroupedDataMetaLoading` reducer body, shared by every grouped slice. Marks
 * a group as loading and clears any prior error so a retry starts from a clean
 * state, while preserving fetched/paging flags for the "Show more" case.
 */
export const setGroupedDataMetaLoadingReducer = (
  state: any,
  action: PayloadAction<{ groupKey: string; attemptId: number }>,
) => {
  const { groupKey, attemptId } = action.payload;
  const existing = state.groupedDataMeta[groupKey] as GroupedDataMetaItem | undefined;
  const nextMeta: GroupedDataMetaItem = {
    isFetched: existing?.isFetched ?? false,
    currentPage: existing?.currentPage ?? 0,
    hasMore: existing?.hasMore ?? false,
    isLoading: true,
    hasError: false,
    // Stamp the attempt taking ownership of this group. The fulfilled/rejected
    // reducers apply a settle only when its `_attempt` matches this value.
    attemptId,
  };
  state.groupedDataMeta[groupKey] = nextMeta;
};

type TagData = {
  tag_id: string;
  [key: string]: any;
};

// these are event which are related to new item creation in the system
// eg: quote created, service request created, work order created etc.
const NEW_ITEM_CREATION_EVENTS = [
  'quote.created',
  'serviceRequest.created',
  'serviceRequest.workOrder.created',
];
export type NewItemCreationEvent =
  | 'quote.created'
  | 'serviceRequest.created'
  | 'serviceRequest.workOrder.created';

export const updateDataByGroupOptions = (
  state: any,
  action: PayloadAction<{
    response: { data: Array<any>; meta: any };
    groupByOptionsList: Array<string>;
  }>,
  idKey: string,
) => {
  const { response, groupByOptionsList } = action.payload;

  const isKeyIncludedInGroupOptionsList = (key: string) => groupByOptionsList?.includes(key);
  const newData = {
    ...(Number(response?.meta?.current_page || 0) > 1 ? state.groupedData || {} : {}),
  };
  response?.data?.forEach((obj) => {
    const groupOptionKey = obj?.group;
    if (groupOptionKey && isKeyIncludedInGroupOptionsList(groupOptionKey)) {
      let newDataIds = [...(newData[groupOptionKey] || [])];

      // checking new data id is already exist in the group. if exist,
      // then we need to remove and add it to the exact position.
      if (obj?.[idKey] && newDataIds?.includes(obj?.[idKey])) {
        // removing the data id.
        newDataIds = newDataIds?.filter((id) => id !== obj?.[idKey]);
      }
      // adding the new data id in order.
      newDataIds.push(obj?.[idKey]);
      newData[groupOptionKey] = newDataIds;
    }
  });

  state.groupedData = newData;
};

export const addToListOrReorderTheList = (
  state: any,
  action: PayloadAction<{
    id: string;
    isGroupBySelected: boolean;
    data: { [key: string]: any };
    listKey: 'work_orders' | 'service_requests' | 'quotes';
    pusherEvent: NewItemCreationEvent;
    keyShouldNotUpdated?: 'tags';
  }>,
) => {
  const {
    id,
    isGroupBySelected,
    data,
    keyShouldNotUpdated = '',
    listKey,
    pusherEvent,
  } = action.payload;

  const reduxDetailsIdKey = {
    work_orders: 'workOrderUuid',
    service_requests: 'serviceRequestUuid',
    quotes: 'quoteId',
  };

  if (id) {
    const isDataDetailsId = state?.showSidePanel?.[reduxDetailsIdKey?.[listKey]];

    if (isDataDetailsId !== id && data) {
      state.source[id] = {
        ...data,
        ...(keyShouldNotUpdated === 'tags'
          ? { tags: state.source?.[id]?.tags || [] }
          : { tags: (data?.tags || []).map((tag: TagData) => tag?.tag_id) }),
        trips: processTrips(data?.trips || []),
      };
    }
    if (isDataDetailsId === id && data) {
      state.showSidePanel = {
        ...(state.showSidePanel || {}),
        ...(keyShouldNotUpdated ? { needToRefetch: false } : { needToRefetch: true }),
      };
    }

    // If Grouping enabled?
    if (isGroupBySelected) {
      // Check the data id contains in the any group,
      // if find any group.
      const currentGroupKey = Object.keys(state?.groupedData || {})?.find((key) =>
        state?.groupedData?.[key]?.includes(id),
      );

      if (currentGroupKey) {
        // If the current group key is not matched with upcoming group.
        if (data?.group && currentGroupKey !== data?.group) {
          // delete the data id from previous group and add it in new group.
          state.groupedData = {
            ...(state.groupedData || {}),
            [currentGroupKey]: state?.groupedData?.[currentGroupKey]?.filter(
              (groupId: string) => groupId !== id,
            ),
            [data?.group]: [...(state?.groupedData?.[data?.group] || []), id],
          };
        }
      } else if (data?.group) {
        // Add the data id to the group
        state.groupedData = {
          ...(state.groupedData || {}),
          [data?.group]: [...(state?.groupedData?.[data?.group] || []), id],
        };
      }
    } else {
      const isIdIncludesInTheList = state?.data?.data?.some((itemId: string) => itemId === id);
      // Check data id contains in the list?, if not add it to the list.
      if (!isIdIncludesInTheList) {
        if (NEW_ITEM_CREATION_EVENTS?.includes(pusherEvent)) {
          state.data.data.unshift(id);
        } else {
          state.data.data.push(id);
        }
      }
    }
  }
};

export const removeDataIdFromList = (state: any, action: PayloadAction<string>) => {
  const dataId = action.payload;
  if (dataId && state.data.data) {
    state.data = {
      ...(state.data || {}),
      data: state?.data?.data?.filter((id: string) => id !== dataId),
    };
  }
  if (dataId && state.groupedData) {
    Object.keys(state.groupedData || {})?.forEach((key) => {
      state.groupedData[key] = state?.groupedData?.[key]?.filter((id: string) => id !== dataId);
    });
  }
};
